import { Children, Comment } from '~/components/discussions';
import useSWRInfinite from 'swr/infinite';
import { getDiscussionsUrl, getComments } from '~/lib/discussions/fetch';
import { Loader } from '~/components/ui';
import { Dropdown } from '@appsumo/dorado-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretUp } from '@fortawesome/free-solid-svg-icons';
import { useState, useEffect, useMemo } from 'react';
import { Transition } from '@headlessui/react';
import useSWR from 'swr';
import {
  Comment as CommentType,
  DiscussionSort,
  DiscussionFilter,
} from '~/lib/discussions/types';

/**
 * Replies to a comment (thread) allowing children to hide/show.
 */
function CommentThread({
  comment,
  mutateComment,
  moderator = false,
  showAllReplies = true,
}: {
  comment: CommentType;
  mutateComment: () => void;
  moderator?: boolean;
  showAllReplies: boolean;
}) {
  const [showReplies, setShowReplies] = useState(showAllReplies);

  // when the show all/hide all replies changes update this node
  useEffect(() => {
    setShowReplies(showAllReplies);
  }, [showAllReplies]);

  return (
    <div key={comment.id}>
      <div className="rounded-lg shadow-lg">
        <Comment
          href={comment.display_path}
          comment={comment}
          moderator={moderator}
          mutateComment={mutateComment}
        />
        {comment.children && comment.children.length > 0 && (
          <div className="border-t bg-gray-100 py-4 px-8 sm:px-12">
            <div className="flex items-end justify-end">
              <button
                className="group inline-flex items-center gap-x-2 text-xs text-gray-500"
                onClick={() => setShowReplies(!showReplies)}
              >
                <FontAwesomeIcon
                  icon={faCaretUp}
                  className={`${showReplies ? '' : 'rotate-180'}`}
                />
                {showReplies ? 'Hide' : 'Show'} replies
                <div className="h-5 w-5 rounded-full border border-gray-500 text-center align-middle text-xs transition delay-150 ease-in-out group-hover:border-yellow-500 group-hover:text-yellow-500">
                  {comment.children.length}
                </div>
              </button>
            </div>
            <Transition
              show={showReplies}
              enter="transition-[max-height] duration-300 ease-in-out overflow-hidden"
              enterFrom="max-h-[0px]"
              enterTo="max-h-none"
              leave="transition-[max-height] duration-300 ease-in-out overflow-hidden"
              leaveFrom="max-h-none"
              leaveTo="max-h-[0px]"
            >
              <Children
                root={comment}
                comments={comment.children}
                moderator={moderator}
                mutateComment={mutateComment}
              />
            </Transition>
          </div>
        )}
      </div>
    </div>
  );
}

const filterOptions = [
  { value: 'all', label: 'All' },
  { value: 'unresolved', label: 'Unresolved' },
  { value: 'unapproved', label: 'Pending' },
];

/**
 * Render a stream of comments or deal reviews on a PDP tab.
 */
export function DealComments({
  dealId,
  fallbackData,
  type,
  sortOptions,
  total,
}: {
  dealId: number;
  fallbackData: any[];
  type: string;
  sortOptions: any[];
  total: number;
}) {
  const { data: userStatus } = useSWR(
    `/comments/api/discussion/user_status/?deal_id=${dealId}`,
  );
  // current sort value
  const [sortBy, setSortBy] = useState<DiscussionSort>(sortOptions[0].value);
  // current filter value
  const [filterBy, setFilterBy] = useState<string>(filterOptions[0].value);
  // holds the state for show all/hide all replies
  const [showReplies, setShowReplies] = useState(true);
  // load comments using infinite loader
  const { data, size, setSize, error, mutate } = useSWRInfinite(
    (pageIndex, previousPageData) => {
      // construct URL for next page of data
      const url = getDiscussionsUrl({
        dealId,
        type,
        page: pageIndex + 1,
        sort: sortBy,
      });
      // TODO: recognise the end of stream and return no more
      if (previousPageData === null) {
        //return null;
      }

      return url;
    },
    {
      fallbackData,
      revalidateFirstPage: false,
      fetcher: getComments,
    },
  );

  // reset paginator when sort changes
  useEffect(() => {
    mutate([], true);
    setSize(1);
  }, [sortBy, mutate, setSize]);

  // construct data for rendering by combing all pages into a single stream of comments
  const comments = data ? [].concat(...data) : [];
  const isLoadingInitialData = typeof data === 'undefined' && !error;
  const isLoadingMore =
    isLoadingInitialData ||
    (size > 0 && data && typeof data[size - 1] === 'undefined');
  const isFinishedPaginating =
    size > 0 && data && data[data.length - 1]?.length === 0;

  // current sort label
  const currentSort = useMemo(
    () =>
      sortOptions.find((s) => s.value === sortBy)?.label ||
      sortOptions[0].label,
    [sortBy, sortOptions],
  );

  // current filter label
  const currentFilter = useMemo(
    () =>
      filterOptions.find((f) => f.value === filterBy)?.label ||
      filterOptions[0].label,
    [filterBy],
  );

  return (
    <>
      <div className="flex w-full items-center max-sm:flex-col max-sm:items-start max-sm:gap-y-4 sm:gap-x-4">
        <Dropdown
          defaultValue={{ label: currentFilter, value: filterBy }}
          options={filterOptions}
          onClick={(value) => setFilterBy(value.value as DiscussionFilter)}
          className="w-44 text-xs max-sm:w-full"
        />
        <Dropdown
          defaultValue={{ label: currentSort, value: sortBy }}
          options={sortOptions}
          onClick={(value) => setSortBy(value.value as DiscussionSort)}
          className="w-44 text-xs max-sm:w-full"
        />
        <div className="flex w-full grow justify-between">
          <button
            onClick={() => setShowReplies(!showReplies)}
            className="text-sm text-blue-600"
          >
            {showReplies ? 'Hide' : 'Show'} all replies
          </button>
          <div>
            Showing <strong>{comments.length}</strong> of{' '}
            <strong>{total}</strong>{' '}
            {type === 'dealcomment' ? 'questions' : 'reviews'}
          </div>
        </div>
      </div>
      {comments.map((comment: any) => (
        <CommentThread
          key={comment.id}
          comment={comment}
          showAllReplies={showReplies}
          moderator={userStatus && userStatus.user_moderator}
          mutateComment={mutate}
        />
      ))}
      {!isLoadingMore && !isFinishedPaginating && (
        <div className="my-5 flex items-center justify-center">
          <button
            className="mt-2 rounded-full bg-yellow-400 py-2 px-4 text-xs font-medium"
            onClick={() => setSize(size + 1)}
          >
            Show more {type === 'dealcomment' ? 'questions' : 'reviews'}
          </button>
        </div>
      )}
      {isLoadingMore && (
        <div className="my-5 flex items-center justify-center">
          <Loader />
        </div>
      )}
    </>
  );
}
